/**
 * Utility function to get translated property value based on current language
 * @param obj - The object containing language-specific properties
 * @param field - The base field name (e.g., 'name', 'description')
 * @param language - Current language code
 * @returns The translated value or the default value
 */
export function getTranslatedField<T extends Record<string, any>>(
  obj: T,
  field: string,
  language: string
): any {
  // Return empty string if object is null or undefined
  if (!obj) {
    return '';
  }
  
  // Define the language priority order
  const languagePriority = ['et', 'ru', 'fi', 'en'];
  
  // 1. Try to get the field for the current language
  const languageField = `${field}_${language}`;
  if (obj[languageField] !== undefined && obj[languageField] !== null) {
    return obj[languageField];
  }

  // 2. If current language field is missing, ALWAYS try the base field next.
  //    (Assuming the base field is the default/English)
  if (obj[field] !== undefined && obj[field] !== null) {
    return obj[field];
  }
  
  // 3. If base field is also missing, try other languages in priority order
  for (const lang of languagePriority) {
    const fallbackField = `${field}_${lang}`;
    if (obj[fallbackField] !== undefined && obj[fallbackField] !== null) {
      return obj[fallbackField];
    }
  }
  
  // 4. Finally, return an empty string if nothing is found
  return '';
}

/**
 * Helper function to check if an object has translations for a specific field
 */
export function hasTranslations(obj: Record<string, any>, field: string): boolean {
  const translationFields = ['et', 'ru', 'fi', 'en'].map(lang => `${field}_${lang}`);
  return translationFields.some(fieldName => obj[fieldName] !== undefined);
}